import { useState, useEffect } from "react"
import { useSearch } from "wouter"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { useToast } from "@/hooks/use-toast"
import { useQuery, useMutation } from "@tanstack/react-query"
import { apiRequest, queryClient } from "@/lib/queryClient"
import { useAccessControl } from "@/hooks/useAccessControl"
import { useAccessErrorHandler } from "@/hooks/useAccessErrorHandler"
import { AccessDeniedModal } from "@/components/AccessDeniedModal"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import type { SocialMediaGoal, InsertSocialMediaGoal } from "@shared/schema"
import { insertSocialMediaGoalSchema } from "@shared/schema"
import { 
  Plus, 
  Edit, 
  Trash2, 
  Target, 
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock,
  XCircle
} from "lucide-react"
import { format, isAfter, isBefore } from "date-fns"
import { de } from "date-fns/locale"

const goalTypes = [
  "followers",
  "engagement_rate", 
  "post_reach",
  "link_clicks",
  "profile_views",
  "mentions",
  "shares",
  "comments",
  "likes",
  "custom"
]

// Platform values matching the schema and SocialMediaGoalsOverview component
const platforms = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "google_business", label: "Google Business" }
]

const statusOptions = [
  "active",
  "completed", 
  "paused",
  "cancelled"
]

const getStatusBadge = (status: string) => {
  switch (status) {
    case "active":
      return <Badge variant="default" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Aktiv</Badge>
    case "completed":
      return <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Abgeschlossen</Badge>
    case "paused":
      return <Badge variant="secondary">Pausiert</Badge>
    case "cancelled":
      return <Badge variant="destructive">Abgebrochen</Badge>
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case "active":
      return <Clock className="h-4 w-4 text-blue-600" />
    case "completed":
      return <CheckCircle className="h-4 w-4 text-green-600" />
    case "paused":
      return <Clock className="h-4 w-4 text-gray-600" />
    case "cancelled":
      return <XCircle className="h-4 w-4 text-red-600" />
    default:
      return <Target className="h-4 w-4" />
  }
}

const getGoalTypeLabel = (goalType: string) => {
  const labels: Record<string, string> = {
    "followers": "Follower",
    "engagement_rate": "Engagement-Rate",
    "post_reach": "Beitrag-Reichweite",
    "link_clicks": "Link-Klicks",
    "profile_views": "Profil-Aufrufe",
    "mentions": "Erwähnungen",
    "shares": "Shares",
    "comments": "Kommentare",
    "likes": "Likes",
    "custom": "Benutzerdefiniert"
  }
  return labels[goalType] || goalType
}

export function Goals() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<SocialMediaGoal | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>("all")
  const [filterPlatform, setFilterPlatform] = useState<string>("all")
  
  const search = useSearch()
  
  // Initialize platform filter from URL search params
  useEffect(() => {
    const params = new URLSearchParams(search)
    const platformParam = params.get('platform')
    if (platformParam && platforms.some(p => p.key === platformParam)) {
      setFilterPlatform(platformParam)
    }
  }, [search])

  const { toast } = useToast()
  const { checkAccess } = useAccessControl()
  const { errorState, handleAccessError, clearError } = useAccessErrorHandler()

  // Check access control for Goals functionality
  const accessControl = checkAccess(false) // Goals require basic subscription, not premium

  // Form for adding/editing goals - using string for deadline to match input[type="date"]
  const form = useForm<{
    description: string
    platform: string
    goalType: string
    targetValue: number
    currentValue: number
    deadline: string
    status: string
  }>({
    resolver: zodResolver(insertSocialMediaGoalSchema.extend({
      deadline: insertSocialMediaGoalSchema.shape.deadline.optional().transform(val => val ? val : "")
    })),
    defaultValues: {
      description: "",
      platform: "",
      goalType: "",
      targetValue: 0,
      currentValue: 0,
      deadline: "",
      status: "active"
    }
  })

  // Fetch goals with platform filtering using custom queryFn to build proper URL with query params
  const { data: goals = [], isLoading } = useQuery({
    queryKey: ['/api/goals', filterPlatform !== "all" ? filterPlatform : "all"],
    queryFn: async () => {
      const platform = filterPlatform !== "all" ? filterPlatform : undefined
      const url = platform ? `/api/goals?platform=${platform}` : '/api/goals'
      const response = await fetch(url)
      if (!response.ok) throw new Error('Failed to fetch goals')
      return response.json()
    },
    select: (data) => data as SocialMediaGoal[]
  })

  // Add goal mutation
  const addGoalMutation = useMutation({
    mutationFn: (data: InsertSocialMediaGoal) => apiRequest('POST', '/api/goals', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals'] })
      setIsAddDialogOpen(false)
      form.reset()
      toast({
        title: "Ziel hinzugefügt",
        description: "Das Social Media Ziel wurde erfolgreich erstellt."
      })
    },
    onError: () => {
      toast({
        title: "Fehler beim Hinzufügen",
        description: "Ziel konnte nicht erstellt werden.",
        variant: "destructive"
      })
    }
  })

  // Update goal mutation
  const updateGoalMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: InsertSocialMediaGoal }) => 
      apiRequest('PUT', `/api/goals/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals'] })
      setEditingGoal(null)
      form.reset()
      toast({
        title: "Ziel aktualisiert",
        description: "Die Änderungen wurden gespeichert."
      })
    },
    onError: () => {
      toast({
        title: "Fehler beim Aktualisieren",
        description: "Änderungen konnten nicht gespeichert werden.",
        variant: "destructive"
      })
    }
  })

  // Delete goal mutation
  const deleteGoalMutation = useMutation({
    mutationFn: (id: string) => apiRequest('DELETE', `/api/goals/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals'] })
      toast({
        title: "Ziel gelöscht",
        description: "Das Ziel wurde erfolgreich entfernt."
      })
    },
    onError: () => {
      toast({
        title: "Fehler beim Löschen",
        description: "Ziel konnte nicht gelöscht werden.",
        variant: "destructive"
      })
    }
  })

  const handleEdit = (goal: SocialMediaGoal) => {
    setEditingGoal(goal)
    form.reset({
      description: goal.description || "",
      platform: goal.platform,
      goalType: goal.goalType,
      targetValue: goal.targetValue,
      currentValue: goal.currentValue || 0,
      deadline: goal.deadline ? format(new Date(goal.deadline), "yyyy-MM-dd") : "",
      status: goal.status || "active"
    })
  }

  const handleSubmit = (data: any) => {
    // Schema handles date coercion automatically via z.coerce.date()
    if (editingGoal) {
      updateGoalMutation.mutate({ id: editingGoal.id, data })
    } else {
      addGoalMutation.mutate(data)
    }
  }

  const handleDelete = (id: string) => {
    if (confirm("Sind Sie sicher, dass Sie dieses Ziel löschen möchten?")) {
      deleteGoalMutation.mutate(id)
    }
  }

  const handleDialogClose = () => {
    setIsAddDialogOpen(false)
    setEditingGoal(null)
    form.reset()
  }

  // Filter goals
  const filteredGoals = goals
    .filter(goal => filterStatus === "all" || goal.status === filterStatus)
    .filter(goal => filterPlatform === "all" || goal.platform === filterPlatform)

  // Calculate stats
  const activeGoals = goals.filter(g => g.status === "active").length
  const completedGoals = goals.filter(g => g.status === "completed").length
  const overallProgress = goals.length > 0 ? 
    goals.reduce((sum, goal) => {
      const progress = goal.targetValue > 0 ? ((goal.currentValue || 0) / goal.targetValue) * 100 : 0
      return sum + Math.min(progress, 100)
    }, 0) / goals.length : 0

  return (
    <div className="container max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight font-playfair">Social Media Ziele</h1>
        <p className="text-muted-foreground">
          Verfolgen Sie Ihre Social Media Marketing-Ziele und messen Sie den Erfolg Ihrer Kampagnen
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Aktive Ziele</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeGoals}</div>
            <p className="text-xs text-muted-foreground">Laufende Ziele</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Abgeschlossen</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedGoals}</div>
            <p className="text-xs text-muted-foreground">Erreichte Ziele</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesamtfortschritt</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Math.round(overallProgress)}%</div>
            <Progress value={overallProgress} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Plattformen</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(goals.map(goal => goal.platform)).size}
            </div>
            <p className="text-xs text-muted-foreground">Verschiedene Plattformen</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Add Button */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-48" data-testid="select-filter-status">
              <SelectValue placeholder="Status filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="completed">Abgeschlossen</SelectItem>
              <SelectItem value="paused">Pausiert</SelectItem>
              <SelectItem value="cancelled">Abgebrochen</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterPlatform} onValueChange={setFilterPlatform}>
            <SelectTrigger className="w-full sm:w-48" data-testid="select-filter-platform">
              <SelectValue placeholder="Plattform filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Plattformen</SelectItem>
              {platforms.map(platform => (
                <SelectItem key={platform.key} value={platform.key}>{platform.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Add Goal Button - separate from Dialog to control access */}
        <Button 
          className="w-full sm:w-auto" 
          onClick={() => {
            if (!accessControl.hasAccess) {
              handleAccessError({
                status: accessControl.errorCode === 'SUBSCRIPTION_REQUIRED' ? 402 : 403,
                data: {
                  error: accessControl.reason,
                  code: accessControl.errorCode,
                  subscriptionStatus: accessControl.subscriptionStatus,
                  subscriptionTier: accessControl.subscriptionTier,
                  upgradeRequired: accessControl.upgradeRequired,
                  premiumRequired: accessControl.premiumRequired,
                  supportContact: accessControl.errorCode === 'ACCOUNT_DISABLED'
                }
              })
              return
            }
            setIsAddDialogOpen(true)
          }} 
          data-testid="button-add-goal"
        >
          <Plus className="mr-2 h-4 w-4" />
          Ziel hinzufügen
        </Button>

        <Dialog open={isAddDialogOpen || !!editingGoal} onOpenChange={(open) => {
          if (!open) handleDialogClose()
        }}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editingGoal ? "Ziel bearbeiten" : "Neues Ziel hinzufügen"}
              </DialogTitle>
              <DialogDescription>
                Setzen Sie sich messbare Social Media Ziele und verfolgen Sie Ihren Fortschritt.
              </DialogDescription>
            </DialogHeader>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                {/* Goal title is computed from platform + goalType */}

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Beschreibung</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Beschreiben Sie Ihr Ziel und Ihre Strategie..."
                          rows={3}
                          data-testid="textarea-goal-description"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="platform"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Plattform *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value || ""}>
                          <FormControl>
                            <SelectTrigger data-testid="select-goal-platform">
                              <SelectValue placeholder="Plattform wählen" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {platforms.map(platform => (
                              <SelectItem key={platform.key} value={platform.key}>
                                {platform.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="goalType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ziel-Typ *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value || ""}>
                          <FormControl>
                            <SelectTrigger data-testid="select-goal-type">
                              <SelectValue placeholder="Typ wählen" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {goalTypes.map(type => (
                              <SelectItem key={type} value={type}>
                                {getGoalTypeLabel(type)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="targetValue"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Zielwert *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            placeholder="1000"
                            data-testid="input-goal-target"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="currentValue"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Aktueller Wert</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            data-testid="input-goal-current"
                            {...field}
                            onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="deadline"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Deadline</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            data-testid="input-goal-deadline"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value || "active"}>
                        <FormControl>
                          <SelectTrigger data-testid="select-goal-status">
                            <SelectValue placeholder="Status wählen" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Aktiv</SelectItem>
                          <SelectItem value="completed">Abgeschlossen</SelectItem>
                          <SelectItem value="paused">Pausiert</SelectItem>
                          <SelectItem value="cancelled">Abgebrochen</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={handleDialogClose}
                    data-testid="button-cancel-goal"
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    type="submit"
                    disabled={addGoalMutation.isPending || updateGoalMutation.isPending}
                    data-testid="button-save-goal"
                  >
                    {addGoalMutation.isPending || updateGoalMutation.isPending ? "Speichert..." : 
                     editingGoal ? "Aktualisieren" : "Hinzufügen"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Goals Grid */}
      <div className="grid gap-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredGoals.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Target className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Ziele gefunden</h3>
              <p className="text-muted-foreground text-center mb-4">
                {filterStatus !== "all" || filterPlatform !== "all" ? 
                  "Keine Ziele entsprechen den aktuellen Filtern." :
                  "Erstellen Sie Ihr erstes Social Media Ziel, um loszulegen!"
                }
              </p>
              {filterStatus === "all" && filterPlatform === "all" && (
                <Button onClick={() => setIsAddDialogOpen(true)} data-testid="button-add-first-goal">
                  <Plus className="mr-2 h-4 w-4" />
                  Erstes Ziel erstellen
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredGoals.map((goal) => {
              const progress = goal.targetValue > 0 ? ((goal.currentValue || 0) / goal.targetValue) * 100 : 0
              const progressClamped = Math.min(progress, 100)
              const isOverdue = goal.deadline && isAfter(new Date(), new Date(goal.deadline)) && goal.status === "active"
              
              return (
                <Card key={goal.id} className={`relative ${isOverdue ? 'border-red-200 dark:border-red-800' : ''}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(goal.status || "active")}
                          <CardTitle className="text-lg">
                            {goal.platform} - {getGoalTypeLabel(goal.goalType)}
                          </CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {goal.platform}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {getGoalTypeLabel(goal.goalType)}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(goal)}
                          data-testid={`button-edit-goal-${goal.id}`}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(goal.id)}
                          data-testid={`button-delete-goal-${goal.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {goal.description && (
                      <p className="text-sm text-muted-foreground">{goal.description}</p>
                    )}
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Fortschritt</span>
                        <span className="font-medium">
                          {(goal.currentValue || 0).toLocaleString()} / {goal.targetValue.toLocaleString()}
                        </span>
                      </div>
                      <Progress value={progressClamped} className="h-2" />
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{Math.round(progressClamped)}% erreicht</span>
                        {goal.deadline && (
                          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                            {format(new Date(goal.deadline), "dd.MM.yyyy", { locale: de })}
                            {isOverdue && " (überfällig)"}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      {getStatusBadge(goal.status || "active")}
                      <div className="text-xs text-muted-foreground">
                        {goal.createdAt && format(new Date(goal.createdAt), "dd.MM.yyyy", { locale: de })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      {/* Access Control Error Modal */}
      <AccessDeniedModal
        isOpen={errorState.isOpen}
        onClose={clearError}
        errorCode={errorState.errorCode}
        errorMessage={errorState.errorMessage}
        subscriptionStatus={errorState.subscriptionStatus}
        subscriptionTier={errorState.subscriptionTier}
        expiresAt={errorState.expiresAt}
        upgradeRequired={errorState.upgradeRequired}
        premiumRequired={errorState.premiumRequired}
        supportContact={errorState.supportContact}
      />
    </div>
  )
}